import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Clock,
  CheckCircle2,
  DollarSign,
  Truck,
  FileText,
  ChevronRight,
  RefreshCw,
  Activity,
  Brain,
  LogOut,
  Building2,
  History,
  Eye,
  Loader2,
  Mail,
  Lock,
  KeyRound,
  Settings,
  ClipboardList,
  Plus,
  Trash2,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import ForcedPasswordChangeModal from "@/components/ForcedPasswordChangeModal";
import SupplierSettingsModal from "@/components/SupplierSettingsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================
// SUPPLIER LOGIN PAGE
// ==================
export function SupplierLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Forced password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [supplierName, setSupplierName] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("supplier_token");
    if (token) {
      navigate("/supplier/dashboard");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Try the new temp password auth endpoint first
      let res;
      let data;
      try {
        res = await fetch(`${API_URL}/api/auth/supplier/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password })
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Login failed");
      } catch (newEndpointError) {
        // Fallback to old endpoint
        res = await fetch(`${API_URL}/api/suppliers/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password })
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Login failed");
      }
      
      // Check if password change is required (temp password)
      if (data.force_password_change) {
        setTempToken(data.token);
        setSupplierName(data.supplier?.name || data.supplier?.contact_name || "");
        // Store supplier info for after password change
        localStorage.setItem("supplier_info", JSON.stringify(data.supplier));
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      // Set localStorage items synchronously
      localStorage.setItem("supplier_token", data.token);
      localStorage.setItem("supplier_info", JSON.stringify(data.supplier));
      
      toast.success("Login successful!");
      
      // Small delay to ensure localStorage is written before redirect
      setTimeout(() => {
        window.location.replace("/supplier/dashboard");
      }, 100);
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };
  
  const handlePasswordChangeSuccess = (newToken) => {
    // Password changed, save token and redirect
    localStorage.setItem("supplier_token", newToken);
    setShowPasswordChange(false);
    toast.success("Password changed! Welcome to your dashboard.");
    setTimeout(() => {
      window.location.replace("/supplier/dashboard");
    }, 100);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="supplier-login-page">
      {/* ============ LEFT — HERO WAREHOUSE PHOTO ============ */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="/api/static/email_assets/login_supplier_warehouse.jpg"
          alt="Sterile Mate supplier warehouse team"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a35]/72 via-[#0a1a35]/45 to-[#003366]/60" />
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full text-white">
          <div>
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/40">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-cyan-200">
                  Sterile Mate · Powered by AskIsaac
                </p>
                <p className="text-sm text-white/80">Supplier Partner Portal</p>
              </div>
            </Link>
          </div>

          <div className="max-w-lg">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
              Welcome partner
            </p>
            <h2 className="text-4xl xl:text-5xl font-black leading-[1.05] mb-5 drop-shadow-xl">
              Get paid faster.<br />Ship smarter.
            </h2>
            <p className="text-lg text-white/90 leading-relaxed drop-shadow max-w-md mb-6">
              Sign in to answer RFQs, upload shipping updates, submit invoices, and see every
              opportunity our hospital network sends your way — from Level-1 trauma centers to
              rural critical-access sites.
            </p>

            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "2h", l: "avg RFQ turnaround" },
                { k: "208+", l: "hospitals in network" },
                { k: "$0", l: "listing fees" },
              ].map((s) => (
                <div key={s.l} className="rounded-lg bg-white/10 backdrop-blur border border-white/20 px-3 py-2.5">
                  <p className="text-xl font-black text-white leading-none">{s.k}</p>
                  <p className="text-[10px] font-medium text-white/70 mt-1 uppercase tracking-wider leading-tight">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              W-9 &amp; COI verified
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              QBO sync
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              Same-day PO
            </span>
          </div>
        </div>
      </div>

      {/* ============ RIGHT — LOGIN FORM ============ */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Mobile-only logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center shadow">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <p className="text-lg font-black text-slate-900">Supplier Portal</p>
            </Link>
          </div>

          <div>
            <h1 className="text-3xl font-black text-slate-900 mb-2">Supplier Login</h1>
            <p className="text-slate-600">Access your Sterile Mate partner dashboard.</p>
          </div>

          <div className="mt-8 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Supplier Partner Portal</p>
                  <p className="text-sm text-slate-500">For verified network suppliers</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleLogin} className="p-6 space-y-5">
              <div>
                <Label className="text-slate-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@company.com"
                    className="pl-10 bg-white border-slate-200 text-slate-900"
                    autoFocus
                    required
                    data-testid="supplier-login-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 bg-white border-slate-200 text-slate-900"
                    required
                    data-testid="supplier-login-password"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-blue-600 hover:underline mt-2"
                  data-testid="supplier-forgot-password"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white h-11 text-sm font-semibold"
                data-testid="supplier-login-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            {/* First-time user callout */}
            <div className="px-6 pb-5 pt-1">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1.5">
                  New supplier?
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Ready to sell into our hospital network?{" "}
                  <Link to="/register?type=supplier" className="font-semibold text-blue-700 hover:underline">
                    Request access here
                  </Link>{" "}
                  — free listing, no contracts, and we handle payment terms. Approvals typically
                  complete within 24 hours after W-9 &amp; COI upload.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-6 space-y-2">
            <Link to="/login" className="text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1">
              ← Back to main login
            </Link>
            <p className="text-slate-500 text-sm">
              Support · <a href="tel:8009883892" className="text-blue-700 hover:underline font-medium">800.988.3892</a>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        userType="supplier"
        title="Reset Supplier Password"
      />

      {/* Forced Password Change Modal (for temp passwords) */}
      <ForcedPasswordChangeModal
        open={showPasswordChange}
        token={tempToken}
        userType="supplier"
        userName={supplierName}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
}

// ==================
// SUPPLIER SETUP PAGE (Set Password)
// ==================
export function SupplierSetupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await fetch(`${API_URL}/api/suppliers/setup/${token}`);
        
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Invalid setup link");
        }
        
        const data = await res.json();
        setSupplierInfo(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSetup = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/suppliers/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password,
          confirm_password: confirmPassword
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Setup failed");
      }
      
      const data = await res.json();
      
      // Store token and redirect to portal (like Hospital flow)
      if (data.token) {
        localStorage.setItem("supplier_token", data.token);
        if (data.supplier) {
          localStorage.setItem("supplier_user", JSON.stringify(data.supplier));
        }
        toast.success("Account setup complete! Welcome to AskIsaac.");
        navigate("/supplier/dashboard");
      } else {
        toast.success("Account set up successfully! Please log in.");
        navigate("/supplier/login");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <KeyRound className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Setup Link Invalid</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/supplier/login")} variant="outline">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/30">
            <Brain className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome to AskIsaac</h1>
          <p className="text-slate-400">Supplier Portal Setup</p>
        </div>

        {/* Setup Form */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 mb-6">
            <p className="text-cyan-400 text-sm">
              Setting up account for <strong>{supplierInfo?.supplier_name}</strong>
            </p>
            <p className="text-slate-500 text-xs mt-1">{supplierInfo?.email}</p>
          </div>
          
          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <Label className="text-slate-300">Create Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                  required
                  minLength={8}
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                  required
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {submitting ? "Setting up..." : "Complete Setup"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ==================
// SUPPLIER DASHBOARD
// ==================
const StatsCard = ({ icon: Icon, label, value, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-violet-50 text-violet-600"
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 ${colorClasses[color]} rounded-lg flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-slate-500 text-sm">{label}</p>
    </div>
  );
};

export function SupplierDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [stats, setStats] = useState({});
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [activeTab, setActiveTab] = useState("requests");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [proposalForm, setProposalForm] = useState(null);
  const [proposalItems, setProposalItems] = useState([]);
  const [proposalNotes, setProposalNotes] = useState("");
  const [submittingProposal, setSubmittingProposal] = useState(false);

  const token = localStorage.getItem("supplier_token");

  useEffect(() => {
    if (!token) {
      navigate("/supplier/login");
      return;
    }
    fetchDashboard();
  }, [token, navigate]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/suppliers/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("supplier_token");
          localStorage.removeItem("supplier_info");
          navigate("/supplier/login");
          return;
        }
        throw new Error("Failed to fetch dashboard");
      }
      
      const data = await res.json();
      setSupplier(data.supplier);
      setStats(data.stats);
      setQuoteRequests(data.quote_requests);
      setMyQuotes(data.my_quotes);
      setActivityLog(data.activity_log);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("supplier_token");
    localStorage.removeItem("supplier_info");
    navigate("/supplier/login");
    toast.success("Logged out successfully");
  };

  const fetchProjectsForProposals = async () => {
    try {
      const supplierInfo = JSON.parse(localStorage.getItem("supplier_info") || "{}");
      const email = supplierInfo.email || supplier?.email || "";
      const res = await fetch(`${API_URL}/api/project-command/proposals/for-supplier?supplier_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setAvailableProjects(data.projects || []);
        setMyProposals(data.my_proposals || []);
      }
    } catch (e) { /* ignore */ }
  };

  const startProposal = (project) => {
    setProposalForm(project);
    setProposalItems([{ phase: "", proposed_cost: 0, retail_price: 0, included_above: false }]);
    setProposalNotes("");
  };

  const submitProposal = async () => {
    if (!proposalForm) return;
    setSubmittingProposal(true);
    try {
      const supplierInfo = JSON.parse(localStorage.getItem("supplier_info") || "{}");
      const res = await fetch(`${API_URL}/api/project-command/proposals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: proposalForm.id,
          project_name: proposalForm.name,
          supplier_name: supplier?.contact_name || supplierInfo.name || "",
          supplier_email: supplier?.email || supplierInfo.email || "",
          supplier_company: supplier?.company_name || supplier?.name || "",
          cost_line_items: proposalItems,
          notes: proposalNotes
        })
      });
      if (res.ok) {
        toast.success("Proposal submitted successfully!");
        setProposalForm(null);
        setProposalItems([]);
        fetchProjectsForProposals();
      } else {
        toast.error("Failed to submit proposal");
      }
    } catch (e) { toast.error("Error submitting proposal"); }
    setSubmittingProposal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div className="border-l border-slate-200 pl-3">
                <h1 className="text-slate-900 font-bold text-sm md:text-lg">{supplier?.company_name || supplier?.name || "Supplier Portal"}</h1>
                <p className="text-slate-500 text-xs hidden sm:block"></p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 bg-slate-100 rounded-lg border border-slate-200">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="text-xs md:text-sm font-medium text-slate-700 max-w-[100px] md:max-w-none truncate">{supplier?.contact_name}</span>
              </div>
              <TelegramConnect token={token} userType="supplier" />
              <Button 
                variant="outline" 
                onClick={() => setShowSettings(true)} 
                size="sm"
                data-testid="supplier-settings-btn"
              >
                <Settings className="w-4 h-4 md:mr-2" /> 
                <span className="hidden md:inline">Settings</span>
              </Button>
              <Button variant="outline" onClick={handleLogout} size="sm">
                <LogOut className="w-4 h-4 md:mr-2" /> 
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Telegram Invite Banner - shows once per session for non-connected users */}
        <TelegramInviteBanner 
          token={token} 
          userType="supplier" 
          userName={supplier?.contact_name?.split(' ')[0] || supplier?.name?.split(' ')[0]}
        />
        
        {/* Welcome Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center">
              <Truck className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Welcome back, {supplier?.contact_name || supplier?.name}</h2>
              <p className="text-slate-500">View quote requests and manage your submissions</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatsCard icon={FileText} label="Quote Requests" value={stats.total_quote_requests || 0} color="blue" />
          <StatsCard icon={Clock} label="Pending Quotes" value={stats.pending_quotes || 0} color="amber" />
          <StatsCard icon={DollarSign} label="Submitted Quotes" value={stats.submitted_quotes || 0} color="purple" />
          <StatsCard icon={CheckCircle2} label="Accepted Quotes" value={stats.won_quotes || 0} color="green" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white p-1 rounded-lg border border-slate-200 w-fit">
          <button
            onClick={() => setActiveTab("requests")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "requests"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Quote Requests
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "quotes"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            My Quotes
          </button>
          <button
            onClick={() => setActiveTab("activity")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "activity"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            Activity
          </button>
          <button
            onClick={() => { setActiveTab("proposals"); fetchProjectsForProposals(); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "proposals"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
            data-testid="supplier-proposals-tab"
          >
            <ClipboardList className="w-4 h-4 inline mr-2" />
            Project Proposals
          </button>
        </div>

        {/* Quote Requests Tab */}
        {activeTab === "requests" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-slate-900 font-semibold">Pending Quote Requests</h3>
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            {quoteRequests.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-slate-900 font-medium mb-2">No quote requests</h3>
                <p className="text-slate-500">You&apos;ll see new requests here when they&apos;re assigned to you.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {quoteRequests.map((req) => (
                  <div key={req.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-slate-400 text-sm font-mono">#{req.id.substring(0, 8)}</span>
                        <p className="text-slate-900 font-medium">{req.hospital_name || "Hospital"}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        req.priority === "high" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {req.priority?.toUpperCase() || "NORMAL"}
                      </span>
                    </div>
                    
                    <div className="space-y-1 mb-4">
                      {req.items?.slice(0, 2).map((item, idx) => (
                        <p key={idx} className="text-slate-600 text-sm">
                          • {item.item_name} (Qty: {item.quantity})
                        </p>
                      ))}
                      {req.items?.length > 2 && (
                        <p className="text-slate-400 text-sm">+{req.items.length - 2} more items</p>
                      )}
                    </div>
                    
                    <Button 
                      onClick={() => setSelectedRequest(req)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      View & Submit Quote
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Quotes Tab */}
        {activeTab === "quotes" && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Item</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Hospital</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Your Price</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Status</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {myQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500">
                      No quotes submitted yet
                    </td>
                  </tr>
                ) : (
                  myQuotes.map((quote) => (
                    <tr key={quote.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-slate-900">{quote.item_id?.substring(0, 8)}...</td>
                      <td className="p-4 text-slate-600">{quote.request_hospital || "N/A"}</td>
                      <td className="p-4 text-emerald-600 font-mono font-medium">${quote.unit_price?.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          quote.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                          quote.status === "rejected" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {quote.status || "pending"}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 text-sm">
                        {new Date(quote.submitted_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === "activity" && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h3 className="text-slate-900 font-medium">Recent Activity</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {activityLog.length === 0 ? (
                <div className="p-8 text-center">
                  <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No activity yet</p>
                </div>
              ) : (
                activityLog.map((log, idx) => (
                  <div key={idx} className="p-4 flex items-start gap-4 hover:bg-slate-50">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Activity className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 text-sm">{log.details || log.action}</p>
                      <p className="text-slate-400 text-xs mt-1">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Project Proposals Tab */}
        {activeTab === "proposals" && (
          <div className="space-y-6" data-testid="supplier-proposals-view">
            {/* Proposal Form */}
            {proposalForm ? (
              <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-blue-100 bg-blue-50 flex items-center justify-between">
                  <div>
                    <h3 className="text-blue-900 font-medium">Submit Proposal: {proposalForm.name}</h3>
                    <p className="text-blue-600 text-xs mt-1">{proposalForm.hospital_name} | Budget: ${(proposalForm.budget || 0).toLocaleString()}</p>
                  </div>
                  <button onClick={() => setProposalForm(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  {proposalForm.scope_of_work && (
                    <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm text-slate-600">
                      <span className="font-medium text-slate-700">Scope: </span>{proposalForm.scope_of_work}
                    </div>
                  )}
                  <table className="w-full text-sm mb-4" data-testid="proposal-form-table">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Phase / Scope of Work</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-600">Your Proposed Cost</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposalItems.map((item, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="px-3 py-2">
                            <input
                              value={item.phase}
                              onChange={(e) => {
                                const items = [...proposalItems];
                                items[idx] = { ...items[idx], phase: e.target.value };
                                setProposalItems(items);
                              }}
                              placeholder="e.g., Plumbing Materials & Labor"
                              className="w-full border rounded px-2 py-1.5 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            {item.included_above ? (
                              <span className="text-xs text-slate-400 italic block text-right">Included above</span>
                            ) : (
                              <input
                                type="number"
                                value={item.proposed_cost || ""}
                                onChange={(e) => {
                                  const items = [...proposalItems];
                                  items[idx] = { ...items[idx], proposed_cost: parseFloat(e.target.value) || 0 };
                                  setProposalItems(items);
                                }}
                                placeholder="0"
                                className="w-full border rounded px-2 py-1.5 text-sm text-right"
                              />
                            )}
                          </td>
                          <td className="px-1 py-2">
                            <button onClick={() => setProposalItems(proposalItems.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500 p-1">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-3 py-2 font-bold text-slate-700">TOTAL</td>
                        <td className="px-3 py-2 text-right font-bold text-blue-700">
                          ${proposalItems.filter(i => !i.included_above).reduce((s, i) => s + (i.proposed_cost || 0), 0).toLocaleString()}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setProposalItems([...proposalItems, { phase: "", proposed_cost: 0, included_above: false }])}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Line Item
                    </button>
                    <button
                      onClick={() => setProposalItems([...proposalItems, { phase: "", proposed_cost: 0, included_above: true }])}
                      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add "Included Above"
                    </button>
                  </div>
                  <textarea
                    value={proposalNotes}
                    onChange={(e) => setProposalNotes(e.target.value)}
                    placeholder="Additional notes, lead times, payment terms..."
                    className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
                    rows={3}
                  />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setProposalForm(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button
                      onClick={submitProposal}
                      disabled={submittingProposal || proposalItems.filter(i => i.phase).length === 0}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      data-testid="submit-proposal-btn"
                    >
                      {submittingProposal ? "Submitting..." : "Submit Proposal"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Available Projects */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <h3 className="text-slate-900 font-medium">Available Projects</h3>
                <p className="text-slate-500 text-xs mt-1">Submit cost proposals for active projects</p>
              </div>
              {availableProjects.length === 0 ? (
                <div className="p-8 text-center">
                  <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No projects available for proposals</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {availableProjects.map((project) => {
                    const existing = myProposals.find(p => p.project_id === project.id);
                    return (
                      <div key={project.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                        <div>
                          <p className="text-slate-900 font-medium text-sm">{project.name}</p>
                          <p className="text-slate-500 text-xs">{project.hospital_name} | Budget: ${(project.budget || 0).toLocaleString()}</p>
                        </div>
                        {existing ? (
                          <span className={`text-xs px-3 py-1 rounded-full ${
                            existing.status === "submitted" ? "bg-amber-100 text-amber-700" :
                            existing.status === "imported" ? "bg-green-100 text-green-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {existing.status === "submitted" ? "Proposal Submitted" : existing.status === "imported" ? "Accepted" : existing.status}
                          </span>
                        ) : (
                          <button
                            onClick={() => startProposal(project)}
                            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
                            data-testid={`submit-proposal-${project.id}`}
                          >
                            Submit Proposal
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* My Proposals */}
            {myProposals.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-slate-900 font-medium">My Submitted Proposals ({myProposals.length})</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {myProposals.map((proposal) => (
                    <div key={proposal.id} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-800 text-sm">{proposal.project_name}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            proposal.status === "submitted" ? "bg-amber-100 text-amber-700" :
                            proposal.status === "imported" ? "bg-green-100 text-green-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{proposal.status}</span>
                          <span className="text-sm font-semibold text-blue-600">${(proposal.total_proposed || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">Submitted {new Date(proposal.submitted_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Request Detail Modal */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Quote Request Details</DialogTitle>
            <DialogDescription className="text-slate-500">
              Request #{selectedRequest?.id.substring(0, 8)} from {selectedRequest?.hospital_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="text-slate-900 font-medium mb-3">Items to Quote</h4>
                <div className="space-y-3">
                  {selectedRequest.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-900">{item.item_name}</p>
                        {item.mfr_number && <p className="text-slate-400 text-xs">MFR: {item.mfr_number}</p>}
                      </div>
                      <span className="text-slate-600">Qty: {item.quantity} {item.quantity_type}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-slate-500 text-sm">
                To submit a quote, you&apos;ll receive a dedicated quote link via email.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Truck className="w-4 h-4" />
            <span className="text-sm">AskIsaac |  | 800.988.3892</span>
          </div>
        </div>
      </footer>
      
      {/* Settings Modal */}
      <SupplierSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        token={token}
      />
    </div>
  );
}

export default SupplierDashboard;
